// Copyright (c) 2020, Picsa Advisory Services (Pty) Ltd. All rights reserved.


import React from 'react'
import PropTypes from 'prop-types'
import { graphql, compose } from 'react-apollo'
import gql from 'graphql-tag'

import {
  Spinner,
  Warning,
  Table,
  SvgIcon
} from '../'

import { PageNavigationButtons } from '../'

import { PayslipDataRow } from './PayslipDataRow'

import {
  decodeBase64,
  PageNavigationControl,
  getPageCursor
} from '../../utils'


class Payslips extends React.PureComponent {
  static propTypes = {
    identityId: PropTypes.string,
    recordsPerPage: PropTypes.number,
    className: PropTypes.string
  }

  static defaultProps = {
    recordsPerPage: 15,
    className: ''
  }

  state = {
    pageNav: new PageNavigationControl()
  }

  render() {
    const { loading, error, payslips, fetchNext, fetchPrevious, refetch, recordsPerPage, className } = this.props

    if (loading) {
      return <Spinner type='tail' />
    }

    if (error) {
      return (
        <Warning className='slds-p-around_medium'>
          Failed to fetch payslip data
        </Warning>
      )
    }

    if (payslips.edges.length === 0) {
      return (
        <div className={className}>
          <Table
            columns={['Payday', 'Basic gross', 'Total gross', 'Total deductions', 'Non-Picsa debt deductions']}
            rows={[
              <tr key='no_payslip_records'>
                <td colSpan={6}>
                  <SvgIcon
                    variant='utility'
                    symbol='info'
                    colour='light'
                    className='slds-m-right_medium'
                  />
                  No records to display.
                </td>
              </tr>
            ]}
            objectRows={true}
          />
        </div>
      )
    } else {
      const  pageNav = this.state.pageNav
      const startCursor = payslips.pageInfo.startCursor
      const totalCount = payslips.totalCount

      return (
        <div className={className}>
          <Table
            columns={['Payday', 'Basic gross', 'Total gross', 'Total deductions', 'Non-Picsa debt deductions']}
            rows={payslips.edges.map(({ node: payslip }) => (
              <PayslipDataRow
                id={payslip.id}
                payday={payslip.payday}
                basicGross={payslip.basicGross}
                totalGross={payslip.totalGross}
                totalDeductions={payslip.totalDeductions}
                nonPicsaDebtDeductions={payslip.nonPicsaDebtDeductions}
                payrollRunId={payslip.payrollrun
                  ? payslip.payrollrun.id
                  : null
                }
                workflowrunDeductingId={payslip.payrollrun
                  ? payslip.payrollrun.workflowrunDeducting
                    ? payslip.payrollrun.workflowrunDeducting.id
                    : null
                  : null
                }
              />
            ))}
            objectRows={true}
          />
          <PageNavigationButtons
            onNextClick={() => pageNav.nextPage(fetchNext)}
            onPreviousClick={() => pageNav.previousPage(fetchPrevious)}
            previousDisabled={pageNav.firstPage}
            nextDisabled={pageNav.lastPage}
            startCursor={startCursor}
            totalCount={totalCount}
            resultsPerPage={recordsPerPage}
            resultsText='payroll runs'
            onPageNumberClick={pageNumber => refetch({ cursor: getPageCursor(pageNumber, recordsPerPage) })}
            className='slds-m-top_small slds-clearfix slds-float_right'
          />
        </div>
      )
    }
  }
}

const QUERY_PAYSLIPS = gql`
  query($identityId: UUID, $recordsPerPage: Int!, $cursor: String) {
    payslips(identity_Id: $identityId, orderBy: "-created_at", first: $recordsPerPage, after: $cursor) {
      totalCount
      edges {
        node {
          id
          payday
          basicGross
          totalGross
          totalDeductions
          nonPicsaDebtDeductions
          loanapplication {
            id
          }
          creditapplications {
            edges {
              node {
                id
              }
            }
          }
          payrollrun {
            id
            workflowrunDeducting {
              id
            }
          }
          identity {
            id
            fullName
          }
        }
      }
      pageInfo {
        endCursor
        startCursor
        hasNextPage
        hasPreviousPage
      }
    }
  }
`

const QUERY_PAYSLIPS_PREV = gql`
  query($identityId: UUID, $recordsPerPage: Int!, $cursor: String) {
    payslips(identity_Id: $identityId, orderBy: "-created_at", last: $recordsPerPage, before: $cursor) {
      totalCount
      edges {
        node {
          id
          payday
          basicGross
          totalGross
          totalDeductions
          nonPicsaDebtDeductions
          loanapplication {
            id
          }
          creditapplications {
            edges {
              node {
                id
              }
            }
          }
          payrollrun {
            id
            workflowrunDeducting {
              id
            }
          }
          identity {
            id
            fullName
          }
        }
      }
      pageInfo {
        endCursor
        startCursor
        hasNextPage
        hasPreviousPage
      }
    }
  }
`

const withData = graphql(QUERY_PAYSLIPS, {
    options: ({ identityId, recordsPerPage = 15, cursor }) => ({
      variables: {
        identityId: identityId
          ? decodeBase64(identityId)
          : null,
        recordsPerPage: recordsPerPage || 15,
        cursor
      },
      fetchPolicy: 'network-only'
    }),
    props: ({ ownProps: { identityId, recordsPerPage = 15 }, data: { loading, error, refetch, payslips, fetchMore } }) => ({
      loading,
      error,
      refetch,
      payslips,
      fetchNext: () => {
        return fetchMore({
          query: QUERY_PAYSLIPS,
          variables: {
            identityId: identityId
              ? decodeBase64(identityId)
              : null,
            cursor: payslips.pageInfo.endCursor,
            recordsPerPage
          },
          updateQuery: (previousResult, { fetchMoreResult }) => {
            const newEdges = fetchMoreResult.payslips.edges
            const pageInfo = fetchMoreResult.payslips.pageInfo

            return {
              payslips: {
                __typename: previousResult.payslips.__typename,
                edges: [...newEdges],
                pageInfo
              }
            }
          }
        })
      },
      fetchPrevious: () => {
        return fetchMore({
          query: QUERY_PAYSLIPS_PREV,
          variables: {
            identityId: identityId
              ? decodeBase64(identityId)
              : null,
            cursor: payslips.pageInfo.startCursor,
            recordsPerPage
          },
          updateQuery: (previousResult, { fetchMoreResult }) => {
            const newEdges = fetchMoreResult.payslips.edges
            const pageInfo = fetchMoreResult.payslips.pageInfo

            return {
              payslips: {
                __typename: previousResult.payslips.__typename,
                edges: [...newEdges],
                pageInfo
              }
            }
          }
        })
      }
    })
  })

export default compose(
  withData,
  // withRouter
)(Payslips)



// WEBPACK FOOTER //
// ./src/components/payslips/index.js